import * as React from 'react';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  TitleComponent,
} from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import { Box, Typography } from '@mui/material';
import { formatInr } from './leadFormatters';

echarts.use([BarChart, GridComponent, TooltipComponent, TitleComponent, SVGRenderer]);

type Props = {
  buyerValue: number;
  sellerValue: number;
  buyerRows?: number;
  sellerRows?: number;
};

const LeadCommercialChart: React.FC<Props> = ({
  buyerValue,
  sellerValue,
  buyerRows = 0,
  sellerRows = 0,
}) => {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const chartInstance = React.useRef<echarts.ECharts | null>(null);
  const total = buyerValue + sellerValue;

  React.useEffect(() => {
    if (!chartRef.current) return;
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, undefined, { renderer: 'svg' });
    }
    const chart = chartInstance.current;

    if (total <= 0) {
      chart.setOption({
        title: { text: 'No compiled sales evidence', left: 'center', top: 'middle', textStyle: { fontSize: 13, color: '#888' } },
        xAxis: { show: false },
        yAxis: { show: false },
        series: [],
      });
      return;
    }

    chart.setOption({
      title: { show: false },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const items = Array.isArray(params) ? params : [params];
          return items
            .map((p) => `${p.name}: ${formatInr(p.value)} (${p.seriesName})`)
            .join('<br/>');
        },
      },
      grid: { left: 48, right: 16, top: 16, bottom: 32 },
      xAxis: {
        type: 'category',
        data: ['Buyer', 'Seller'],
        axisLabel: { fontSize: 12 },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (v: number) => formatInr(v, true),
        },
      },
      series: [
        {
          name: 'Observed value',
          type: 'bar',
          data: [buyerValue, sellerValue],
          itemStyle: {
            color: (params: { dataIndex: number }) =>
              params.dataIndex === 0 ? '#1976d2' : '#ed6c02',
            borderRadius: [4, 4, 0, 0],
          },
          barMaxWidth: 72,
        },
      ],
    });

    const onResize = () => chart.resize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [buyerValue, sellerValue, total]);

  React.useEffect(
    () => () => {
      chartInstance.current?.dispose();
      chartInstance.current = null;
    },
    []
  );

  return (
    <Box>
      <Box ref={chartRef} sx={{ width: '100%', height: 220 }} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Invoice rows: buyer {buyerRows} · seller {sellerRows}
      </Typography>
    </Box>
  );
};

export default LeadCommercialChart;
